import React, { useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";

import { Card, CardBody, CardHeader, Container, Row, Col } from 'reactstrap';
import { TabContent, TabPane } from 'reactstrap';
import {
  CheckCircle, Info, PlusCircle, Target, Briefcase, Inbox
} from 'react-feather';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import { toast } from 'react-toastify';
import Loader from 'react-loader-spinner';
import axios from 'axios';

import Breadcrumb from '../../Common/BreadCrumb/BreadCrumbs.component';
import CreateNewIssue from './Modal/CreateNewIssue.component';
import RedmineTickets from './Tickets/RedmineTickets.component';

import useOrgIdentifer from '@hooks/org_identifier';

const THEME_HEX = '#0d6efd';

const FILTER_META = [
  { key: 'data_issues',     label: 'Data Issue' },
  { key: 'new_site',        label: 'New Site' },
  { key: 'new_requirement', label: 'New Requirement' },
  { key: 'other_issues',    label: 'Other Issue' },
  { key: 'volume_drop',     label: 'Volume Drop' },
];

const COLUMN_META = [
  { key: 'not_started',       title: 'Not Started',       countKey: 'not_started_issues_total_count' },
  { key: 'in_progress',       title: 'In Progress',       countKey: 'in_progress_issues_total_count' },
  { key: 'waiting_on_client', title: 'Waiting on Client', countKey: 'waiting_on_client_issues_total_count' },
];

const TabButton = ({ active, onClick, icon, label, count }) => (
  <button
    type='button'
    onClick={onClick}
    className={`btn d-inline-flex align-items-center border-0 ${active ? 'btn-primary' : 'btn-light text-dark'}`}
    style={{ borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 500, marginRight: 6 }}
  >
    {icon}
    <span className='ms-2'>{label}</span>
    {typeof count === 'number' && (
      <span
        className='ms-2'
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 10,
          background: active ? '#fff' : '#e9ecef',
          color: active ? '#0d6efd' : '#495057',
        }}
      >
        {count}
      </span>
    )}
  </button>
);

const FilterPill = ({ active, label, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='btn d-inline-flex align-items-center border'
    style={{
      borderRadius: 999,
      padding: '6px 14px',
      fontSize: 13,
      fontWeight: 500,
      borderColor: active ? THEME_HEX : '#dee2e6',
      background: active ? THEME_HEX : 'white',
      color: active ? 'white' : '#495057',
      transition: 'all 0.15s',
    }}
  >
    <span
      className='me-2 d-inline-block rounded-circle'
      style={{
        width: 8, height: 8,
        background: active ? 'white' : THEME_HEX,
      }}
    />
    {label}
  </button>
);

const EmptyState = ({ message = 'No tickets here' }) => (
  <div className='d-flex flex-column align-items-center justify-content-center text-muted py-5'>
    <Inbox size={32} className='mb-2 opacity-50' />
    <p className='mb-0' style={{ fontSize: 13 }}>{message}</p>
  </div>
);

const KanbanColumn = ({ title, count, tickets, org_identifier, height = 700 }) => (
  <Card className='shadow-sm border-0 h-100' style={{ borderTop: `4px solid ${THEME_HEX}` }}>
    <CardHeader className='bg-white border-0 d-flex justify-content-between align-items-center pt-3 pb-2'>
      <h6 className='fw-bold mb-0 text-dark'>{title}</h6>
      <span
        style={{
          background: '#e7f1ff',
          color: '#0a58ca',
          fontSize: 12,
          padding: '4px 10px',
          fontWeight: 600,
          borderRadius: 10,
        }}
      >
        {typeof count === 'number' ? count : (tickets?.length || 0)}
      </span>
    </CardHeader>
    <CardBody
      className='px-3 pt-2 pb-3'
      style={{ height, overflowY: 'auto', overflowX: 'hidden' }}
    >
      {tickets?.length > 0 ? (
        tickets.map(each => (
          <RedmineTickets key={each.id} data={each} organization_name={org_identifier} />
        ))
      ) : (
        <EmptyState />
      )}
    </CardBody>
  </Card>
);

export default function Issues({ current_user, current_organization, take_my_org_details }) {

  const history = useHistory();
  const org_identifier = useOrgIdentifer();

  const [accessibleModules, setAccessibleModules] = useState();

  const [data, setData] = useState({});
  const [activities, setActivities] = useState([]);
  const [notStarted, setNotStarted] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [closedIssues, setClosedIssues] = useState([]);
  const [waitingOnClient, setWaitingOnClient] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadToggle, setLoadToggle] = useState(true);
  const [activeTab, setActiveTab] = useState("issues");
  const [filterChecks, setFilterChecks] = useState({
    data_issues: true, new_site: true, new_requirement: true, other_issues: true, volume_drop: true,
  });

  useEffect(() => {
    take_my_org_details(org_identifier);
  }, [org_identifier]);

  useEffect(() => {
    let accessible_modules = current_user?.part_of_org_list?.filter(x => x.identifier == org_identifier).map((y) => y?.accessible_modules).flat();
    setAccessibleModules(accessible_modules);
  }, [current_user]);

  useEffect(() => {
    if (org_identifier === 'promptcloud') {
      history.push('/');
      toast.error("You are not authorized to access this page!");
      setLoading(false);
    }
  }, [org_identifier]);

  const createNewIssueModal = () => {
    setModal(curr => !curr);
  };

  useEffect(() => {
    if (org_identifier) {
      setLoading(true);
      const trueData = Object.entries(filterChecks).filter(([, v]) => v).map(([k]) => k);
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
        },
        data: {},
        params: { selected_trackers: trueData },
      };

      axios.get(`/api/v1/organizations/${org_identifier}/issues?page_type=client&controller_name=issues`, config).then(res => {
        setData(res?.data);
        setActivities(res?.data?.activities?.entries || []);
        setClosedIssues(res?.data?.closed_issues || []);
        setInProgress(res?.data?.in_progress_issues || []);
        setNotStarted(res?.data?.not_started_issues || []);
        setWaitingOnClient(res?.data?.waiting_on_client_issues || []);
        setLoading(false);
      }).catch(err => {
        setLoading(false);
        if (err?.response?.data?.page_auth_failed) {
          history.push("/");
          toast.error(err?.response?.data?.message);
        } else if (!err?.response?.data?.org_auth_failed) {
          toast.error(err?.response?.data?.message);
        }
      });
    }
  }, [filterChecks, loadToggle, org_identifier]);

  const canCreate =
    (current_user?.id && current_user?.is_admin) || accessibleModules?.includes("data_access");

  const openCount =
    (data?.not_started_issues_total_count || 0) +
    (data?.in_progress_issues_total_count || 0) +
    (data?.waiting_on_client_issues_total_count || 0);
  const closedCount = data?.closed_issues_total_count || 0;
  const activitiesCount = activities?.length || 0;

  const columnData = {
    not_started: notStarted,
    in_progress: inProgress,
    waiting_on_client: waitingOnClient,
  };

  return (
    <Container fluid>
      <Breadcrumb parent="Issues" title="Issues " />
      <CreateNewIssue
        data={data}
        modal={modal}
        createNewIssueModal={createNewIssueModal}
        setLoadToggle={setLoadToggle}
        setModal={setModal}
      />

      <Card className='shadow-sm border-0 mb-3'>
        <CardBody className='p-3'>
          <div className='d-flex justify-content-between align-items-center flex-wrap mb-3' style={{ gap: 12 }}>
            <div className='d-flex flex-wrap'>
              <TabButton
                active={activeTab === 'issues'}
                onClick={() => setActiveTab('issues')}
                icon={<Info size={16} />}
                label='Open Issues'
                count={openCount}
              />
              <TabButton
                active={activeTab === 'recentActivities'}
                onClick={() => setActiveTab('recentActivities')}
                icon={<Target size={16} />}
                label='Recent Activities'
                count={activitiesCount}
              />
              <TabButton
                active={activeTab === 'closed'}
                onClick={() => setActiveTab('closed')}
                icon={<CheckCircle size={16} />}
                label='Closed Issues'
                count={closedCount}
              />
            </div>
            {canCreate && (
              <button
                className='btn btn-primary d-inline-flex align-items-center'
                onClick={createNewIssueModal}
              >
                <PlusCircle size={16} className='me-2' /> Create New Issue
              </button>
            )}
          </div>

          {activeTab === 'issues' && (
            <div className='d-flex flex-wrap pt-2 border-top' style={{ gap: 8 }}>
              <span
                className='text-muted fw-bold align-self-center me-2'
                style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Filter:
              </span>
              {FILTER_META.map(f => (
                <FilterPill
                  key={f.key}
                  active={filterChecks[f.key]}
                  label={f.label}
                  onClick={() => setFilterChecks({ ...filterChecks, [f.key]: !filterChecks[f.key] })}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {loading ? (
        <div className='d-flex justify-content-center align-items-center py-5'>
          <Loader type="Puff" color="#0d6efd" height={60} width={60} />
        </div>
      ) : (
        <TabContent activeTab={activeTab}>
          <TabPane tabId="issues">
            <Row className='g-3'>
              {COLUMN_META.map(col => (
                <Col lg={4} key={col.key}>
                  <KanbanColumn
                    title={col.title}
                    count={data?.[col.countKey]}
                    tickets={columnData[col.key]}
                    org_identifier={org_identifier}
                  />
                </Col>
              ))}
            </Row>
          </TabPane>

          <TabPane tabId="recentActivities">
            {activities?.length > 0 ? (
              <VerticalTimeline layout='1-column'>
                {activities.map(each => (
                  <VerticalTimelineElement
                    className="vertical-timeline-element--work"
                    key={each.issue_id}
                    date={each.updated_at || ""}
                    iconStyle={{ background: '#0d6efd', color: 'white' }}
                    icon={<Briefcase />}
                  >
                    <a
                      href={`/organizations/${org_identifier}/issues/${each.issue_id || ''}`}
                      className="vertical-timeline-element-title text-primary fw-semibold"
                      style={{ fontSize: 16 }}
                    >
                      {each.activity_title}
                    </a>
                    <p className='text-muted custom-ptag mt-2 mb-0' dangerouslySetInnerHTML={{ __html: each.content }} />
                  </VerticalTimelineElement>
                ))}
              </VerticalTimeline>
            ) : (
              <Card className='shadow-sm border-0'>
                <CardBody>
                  <EmptyState message='No recent activities' />
                </CardBody>
              </Card>
            )}
          </TabPane>

          <TabPane tabId="closed">
            <Row>
              <Col lg={5}>
                <KanbanColumn
                  title='Closed Issues'
                  count={closedCount}
                  tickets={closedIssues}
                  org_identifier={org_identifier}
                />
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      )}
    </Container>
  );
}
